import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  useDocumentSearch,
  useRoomCategories,
  useRoomFolderOrder,
  useRoomDocuments,
  useSellerRoomAccess,
  useSetStatementBasis,
  useUploadReadableCopy,
} from '@/api/hooks';
import {
  ActiveFiltersBar,
  ApiError,
  AskAnswerSummary,
  AskTheRoomPrompt,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataRoomFolders,
  documentInPhase,
  folderPhaseView,
  type FolderPhaseGroup,
  DEFAULT_DOCUMENT_SORT,
  Dialog,
  DocumentList,
  DocumentViewerModal,
  ErrorText,
  formatDateTime,
  HelpTip,
  Icon,
  NO_DATA_ROOM_FILTERS,
  PersonDisplay,
  DataRoomFilterMenu,
  SearchBar,
  showsStatementBasis,
  StatementBasisEditor,
  ResultsTitle,
  UnreadableFilterChip,
  UnreadableFileOptions,
  documentDownloadPath,
  askAnswerDocuments,
  branchCategoryIds,
  buttonVariants,
  categoryNodesById,
  categoryPathNames,
  categoryParents,
  cn,
  countStatementBases,
  dataRoomFilterChips,
  dataRoomFiltersFrom,
  dataRoomSearch,
  describePeriodKey,
  categoryPathLabel,
  documentSortFrom,
  folderViewTree,
  hasDataRoomFilters,
  hasSearchCriteria,
  narrowDataRoomDocuments,
  periodNodesByCategory,
  sortDocuments,
  useAskTheRoom,
  useDataRoomSearchBox,
  withDocumentSort,
  type DataRoomFilters,
  type DocumentListRow,
  type DocumentSort,
  type RowMenuItem,
} from '@dataroom/shared';
import { SellerDocStatus } from '@/components/SellerDocStatus';
import {
  VersionSuggestionsPanel,
  VersionSuggestionsReview,
} from '@/components/VersionSuggestionsPanel';
import { SellerVersionHistory } from '@/components/SellerVersionHistory';
import { SellerManageDocumentDialog } from '@/components/SellerManageDocumentDialog';
import type { CategoryNode, RoomDocument } from '@dataroom/shared';

/**
 * The seller's "Your documents" — the same data room the admin works in (owner,
 * 2026-10-15: "The seller's document area should look just like the admin's"):
 * the folder tree on the left, and on the right the document list with search,
 * the facet chips, the "Showing: … ✕ Show all documents" bar, sortable columns
 * (date of data, added, name) and each document's filing chips. The filters and
 * the sort live in the URL, so Back and reload keep them. A document's name
 * opens the in-app viewer — what buyers see (the redacted copy once redaction
 * is complete) — and "Details" opens what the seller may do with it: correct
 * the accounting basis, send a readable copy, see its versions. "Manage
 * document" (owner decision 2026-10-21) lets the seller side — the bound seller
 * and the team — move a document to another folder (or add a subfolder for it),
 * re-file it with the AI, and set its period and date of data, exactly as the
 * admin can; each change is saved under their own name. Still not offered:
 * who-sees-what, deleting, the redaction review.
 *
 * The documents are the seller's own listing (the bound seller sees the whole
 * library minus deal paperwork, ADR-0028) — the same API as before.
 */
export function DocumentsPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <DocumentsPageInner roomId={access.roomId} />;
}

function DocumentsPageInner({ roomId }: { roomId: string }) {
  const documents = useRoomDocuments(roomId);
  const categories = useRoomCategories(roomId);
  const folderOrder = useRoomFolderOrder(roomId);

  // Everything that narrows the list — folder, period, search, "Couldn't read",
  // accounting basis — and the sort live in the URL (shared lib/data-room-filters,
  // lib/document-sort). Applying a filter adds a history entry; refining the
  // search text replaces it.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = dataRoomFiltersFrom(searchParams);
  // "Needs a look" is the admin's working filter, never the seller's.
  const filters: DataRoomFilters =
    urlFilters.show === 'needs-look' ? { ...urlFilters, show: null } : urlFilters;
  const setFilters = (patch: Partial<DataRoomFilters>, replace = false) =>
    setSearchParams((prev) => dataRoomSearch({ ...dataRoomFiltersFrom(prev), ...patch }, prev), {
      replace,
    });
  const explicitSort = documentSortFrom(searchParams);
  const setSort = (sort: DocumentSort) => setSearchParams((prev) => withDocumentSort(sort, prev));

  const parentOf = useMemo(() => categoryParents(categories.data ?? []), [categories.data]);
  // The tree as shown: empty template placeholders ("[Account Name]") dropped,
  // every level in the checklist's order, and the checklist's phases above the
  // categories (owner, 2026-10-15: Phase › Category › Sub-category, as the
  // spreadsheet reads) — lib/folder-phases. Display only.
  const folderView = useMemo(
    () => folderPhaseView(folderViewTree(categories.data ?? []), folderOrder.data),
    [categories.data, folderOrder.data],
  );
  const treeNodes = folderView.nodes;
  const phaseGroups = folderView.phases;
  const nodesById = useMemo(() => categoryNodesById(treeNodes), [treeNodes]);
  const selectedId = filters.folder;
  const selected: CategoryNode | null = selectedId ? (nodesById.get(selectedId) ?? null) : null;
  const selectedPeriod = filters.period;
  // A phase row: every folder under it (a folder pick replaces it).
  const selectedPhase: FolderPhaseGroup | null =
    !selectedId && filters.phase
      ? (phaseGroups?.find((p) => p.key === filters.phase) ?? null)
      : null;
  const phaseName = selectedPhase?.name ?? null;
  const folderName = selected?.name ?? (categories.isPending ? 'Folder' : 'A folder');
  const periodNodes = useMemo(
    () => periodNodesByCategory(documents.data ?? [], parentOf, branchCategoryIds(treeNodes)),
    [documents.data, parentOf, treeNodes],
  );
  const periodLabel = selectedPeriod
    ? (describePeriodKey(selectedPeriod)?.fullLabel ?? null)
    : null;
  const query = filters.query;
  const searchBox = useDataRoomSearchBox(query, (q, replace) => setFilters({ query: q }, replace));
  // Focus goes to the list after a filter is removed (its ✕ is gone with it).
  const listRef = useRef<HTMLDivElement>(null);
  const focusList = () => listRef.current?.focus();
  // The results area shows ONE thing — the Ask answer or the folder's documents,
  // whichever the person did last (owner, 2026-10-15). Asking clears the folder
  // and every other narrowing (the answer replaces the list); picking a folder,
  // "All documents" or "Show all documents" clears the answer. The answer is not
  // in the URL: any filter arriving later (Back to a folder view) drops it too.
  const filtered = hasDataRoomFilters(filters);
  const [askSort, setAskSort] = useState<DocumentSort | null>(null);
  const ask = useAskTheRoom(roomId, () => {
    setAskSort(null);
    if (filtered) setFilters(NO_DATA_ROOM_FILTERS);
  });
  const clearAsk = ask.clear;
  useEffect(() => {
    if (filtered) clearAsk();
  }, [filtered, clearAsk]);
  const asking = ask.state.status !== 'idle';
  // Picking a folder starts over: the question and its answer go (owner, 2026-10-22).
  const resetAsk = ask.reset;
  const pickFolder = (patch: Partial<DataRoomFilters>) => {
    resetAsk();
    setFilters(patch);
  };
  const showAll = () => {
    resetAsk();
    if (filtered) setFilters(NO_DATA_ROOM_FILTERS);
    focusList();
  };

  // The details dialog and the viewer, tracked by id so they follow the polled
  // listing (a corrected basis shows at once).
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  // "Manage document" opened from the row menu or the details (the viewer opens its own).
  const [manageId, setManageId] = useState<string | null>(null);
  // "Open the file" on a file we couldn't read: the viewer starts on the original.
  const [previewOriginal, setPreviewOriginal] = useState(false);
  const replace = useUploadReadableCopy(roomId);
  // What the seller can do about a file we couldn't read (ADR-0051 update
  // 2026-10-15): open or download the original, or upload a replacement.
  const unreadableOptionsFor = (doc: RoomDocument, onOpen: (() => void) | undefined) => (
    <UnreadableFileOptions
      document={doc}
      onOpen={doc.downloadReady ? onOpen : undefined}
      downloadHref={doc.downloadReady ? documentDownloadPath(roomId, doc.id) : undefined}
      onReplace={(body) => replace.mutateAsync({ documentId: doc.id, body })}
    />
  );
  const openOriginal = (documentId: string) => {
    setDetailsId(null);
    setPreviewOriginal(true);
    setPreviewId(documentId);
  };
  // A financial statement's accounting basis (ADR-0053): the seller may correct it.
  const setBasis = useSetStatementBasis(roomId);
  const openDetails = (documentId: string) => {
    setBasis.reset();
    setDetailsId(documentId);
  };

  const liveById = useMemo(
    () => new Map((documents.data ?? []).map((d) => [d.id, d] as const)),
    [documents.data],
  );
  const openDocument = (doc: Pick<RoomDocument, 'id' | 'downloadReady'>) =>
    (liveById.get(doc.id) ?? doc).downloadReady ? setPreviewId(doc.id) : openDetails(doc.id);
  const basisCounts = useMemo(() => countStatementBases(documents.data ?? []), [documents.data]);
  const unreadableOnly = filters.show === 'unreadable';
  const basisFilter = filters.basis;

  // Free text is a server search; the selected folder is narrowed below by the
  // tree's own rule (`inFolder`) over the permission-scoped listing, not by the
  // server's tag facet — which also matches a document's edges on other branches.
  const searchQuery = { query };
  const searchActive = hasSearchCriteria(searchQuery);
  const search = useDocumentSearch(roomId, searchQuery);

  const unauthorized = [documents.error, categories.error, search.error].some(
    (e) => e instanceof ApiError && e.status === 401,
  );
  if (unauthorized) return <Navigate to="/login" replace />;

  const listPending = searchActive ? search.isPending : documents.isPending;
  const listError = searchActive ? search.error : documents.error;
  const found: RoomDocument[] = searchActive ? (search.data ?? []) : (documents.data ?? []);
  const unreadableCount = (documents.data ?? []).filter((d) => d.unreadable).length;
  const { listed, visible: narrowed } = narrowDataRoomDocuments(found, filters, {
    live: (d) => liveById.get(d.id),
    parentOf,
    inPhase: selectedPhase ? (d) => documentInPhase(d, selectedPhase, parentOf) : undefined,
  });
  // Sorted as the URL says; by default newest additions first, and a text search's
  // best match first.
  const sort = explicitSort ?? (query ? null : DEFAULT_DOCUMENT_SORT);
  const visible = sort ? sortDocuments(narrowed, sort) : narrowed;
  const heading = query
    ? 'Search results'
    : selectedId
      ? periodLabel
        ? `${folderName} · ${periodLabel}`
        : folderName
      : (phaseName ?? 'All documents');
  const folderLabel = selectedId
    ? periodLabel
      ? `${folderName} › ${periodLabel}`
      : folderName
    : (phaseName ?? 'All documents');

  const rowFor = (doc: RoomDocument): DocumentListRow => {
    const live = liveById.get(doc.id) ?? doc;
    const actions: RowMenuItem[] = [
      ...(live.downloadReady
        ? [{ label: 'View file', icon: 'eye' as const, onSelect: () => setPreviewId(doc.id) }]
        : []),
      ...(live.aiClassifiable === false
        ? []
        : [
            {
              label: 'Manage document',
              icon: 'folder' as const,
              onSelect: () => setManageId(doc.id),
            },
          ]),
      { label: 'Details', icon: 'file' as const, onSelect: () => openDetails(doc.id) },
    ];
    return {
      doc,
      live,
      status: () => <SellerDocStatus status={live.status} unreadable={live.unreadable} />,
      actions,
      onManage: live.aiClassifiable === false ? undefined : () => setManageId(doc.id),
      unreadableOptions: live.unreadable
        ? unreadableOptionsFor(live, () => openOriginal(doc.id))
        : undefined,
      openTitle: live.downloadReady ? 'View file' : 'Details',
      onOpen: () => openDocument(doc),
    };
  };
  const rows = visible.map(rowFor);
  // The answer's rows: the same rows as the list, in the answer's order until sorted.
  const answered = askAnswerDocuments(ask.state);
  const askRows = (askSort ? sortDocuments(answered, askSort) : answered).map(rowFor);
  // What the results area shows, in words (its title): "Answer: …", "Folder:
  // Financial › Bank Statements › 2024", "All documents".
  const folderPath = selectedId ? categoryPathNames(treeNodes, selectedId) : [];
  const resultsTitle =
    ask.state.status !== 'idle'
      ? `Answer: ${ask.state.question}`
      : query
        ? 'Search results'
        : selectedId
          ? `Folder: ${[...(folderPath.length > 0 ? folderPath : [folderName]), ...(periodLabel ? [periodLabel] : [])].join(' › ')}`
          : phaseName
            ? `Folder: ${phaseName}`
            : 'All documents';
  const listName = ask.state.status !== 'idle' ? `Answer to “${ask.state.question}”` : heading;

  const filterChips = dataRoomFilterChips(
    filters,
    { folderName, periodLabel, phaseName },
    (patch) => {
      setFilters(patch);
      focusList();
    },
  );
  const roomTotal = documents.data ? documents.data.length : null;

  const details = detailsId ? (liveById.get(detailsId) ?? null) : null;
  const previewDoc = previewId ? (liveById.get(previewId) ?? null) : null;
  const managedDoc = manageId ? (liveById.get(manageId) ?? null) : null;

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <DataRoomFolders
          nodes={treeNodes}
          pending={categories.isPending}
          selectedId={selectedId}
          selectedPeriod={selectedPeriod}
          periodNodes={periodNodes}
          onSelect={(node) => pickFolder({ folder: node?.id ?? null, period: null, phase: null })}
          onSelectPeriod={(node, periodKey) =>
            pickFolder({ folder: node.id, period: periodKey, phase: null })
          }
          label={folderLabel}
          headerAction={<HelpTip id="seller.documents.folders" />}
          phases={phaseGroups}
          selectedPhase={selectedPhase?.key ?? null}
          onSelectPhase={(phase) => pickFolder({ phase: phase.key, folder: null, period: null })}
        />

        <div className="min-w-0 space-y-6">
          {/* "Possible new version of …" (ADR-0031): an upload kept as its own
              document because it was not clearly the same — the seller decides too. */}
          <VersionSuggestionsReview roomId={roomId} onOpenDocument={openDetails} />

          {/* Ask the Data Room (ADR-0035), and directly below it the ONE results
              area: the answer, or the selected folder's documents (owner, 2026-10-15). */}
          <div className="space-y-3">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-1.5">
                  <CardTitle>Ask the Data Room</CardTitle>
                  <HelpTip id="seller.documents.ask" />
                </div>
              </CardHeader>
              <CardContent>
                <AskTheRoomPrompt ask={ask} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <ResultsTitle
                    title={resultsTitle}
                    onClear={asking || filtered ? showAll : undefined}
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/rooms/${roomId}/processing`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Processing status
                    </Link>
                    <Link to={`/rooms/${roomId}`} className={cn(buttonVariants({ size: 'sm' }))}>
                      <Icon name="upload" />
                      Add documents
                    </Link>
                  </div>
                </div>
                {asking ? (
                  <div className="pt-2" aria-live="polite">
                    <AskAnswerSummary state={ask.state} />
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-2 pt-2">
                      <SearchBar
                        key={searchBox.key}
                        initialQuery={query}
                        onQueryChange={searchBox.onQueryChange}
                        searching={search.isFetching}
                        className="min-w-0 flex-1"
                      />
                      <div className="flex shrink-0 items-center gap-1">
                        <DataRoomFilterMenu
                          basisCounts={basisCounts}
                          basis={basisFilter}
                          onBasisChange={(basis) => setFilters({ basis })}
                        />
                        {/* The menu offers nothing until a statement has an accounting basis. */}
                        {basisFilter || Object.values(basisCounts).some((n) => n > 0) ? (
                          <HelpTip id="seller.documents.filter-menu" />
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-end gap-1">
                      <UnreadableFilterChip
                        count={unreadableCount}
                        active={unreadableOnly}
                        onToggle={() => setFilters({ show: unreadableOnly ? null : 'unreadable' })}
                        title="Files we couldn't read — please send a readable copy of each"
                      />
                      {unreadableCount > 0 || unreadableOnly ? (
                        <HelpTip id="seller.documents.unreadable-chip" className="mb-1" />
                      ) : null}
                    </div>
                    <ActiveFiltersBar
                      chips={filterChips}
                      shown={listPending || listError ? null : rows.length}
                      total={roomTotal}
                      onShowAll={showAll}
                    />
                  </>
                )}
              </CardHeader>
              <CardContent
                ref={listRef}
                tabIndex={-1}
                role="region"
                aria-label={`${listName}: document list`}
                className="outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring max-md:px-3"
              >
                {asking ? (
                  askRows.length > 0 ? (
                    <DocumentList
                      rows={askRows}
                      sort={askSort}
                      onSort={setAskSort}
                      audience="seller"
                      manageAside={<HelpTip id="seller.documents.manage-link" />}
                    />
                  ) : null
                ) : listPending ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : listError ? (
                  <ErrorText error={listError} />
                ) : rows.length > 0 ? (
                  <DocumentList
                    rows={rows}
                    sort={sort}
                    onSort={setSort}
                    audience="seller"
                    manageAside={<HelpTip id="seller.documents.manage-link" />}
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {basisFilter && listed.length > 0
                        ? 'No documents here match that accounting basis.'
                        : unreadableOnly && listed.length > 0
                          ? 'Every file here could be read.'
                          : searchActive
                            ? 'No documents match your search.'
                            : filtered
                              ? 'No documents here yet.'
                              : 'No documents yet. Add files and the AI files each one into the right folder.'}
                    </p>
                    {filtered ? (
                      <Button size="sm" onClick={showAll}>
                        <Icon name="close" size={14} />
                        Show all documents
                      </Button>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {details ? (
        <Dialog title="Document details" onClose={() => setDetailsId(null)} size="md">
          <SellerDocumentDetails
            roomId={roomId}
            document={details}
            onOpenDocument={openDetails}
            onView={details.downloadReady ? () => setPreviewId(details.id) : undefined}
            onManage={details.aiClassifiable === false ? undefined : () => setManageId(details.id)}
            basis={setBasis}
            unreadableOptions={
              details.unreadable
                ? unreadableOptionsFor(details, () => openOriginal(details.id))
                : undefined
            }
          />
        </Dialog>
      ) : null}

      {/* The in-app viewer: what buyers see (owner, 2026-10-15 — everyone in the
          data room sees the same redactions). */}
      {previewDoc ? (
        <DocumentViewerModal
          key={previewDoc.id}
          roomId={roomId}
          document={previewDoc}
          onClose={() => {
            setPreviewId(null);
            setPreviewOriginal(false);
          }}
          audience="seller"
          redactedCaptionAside={<HelpTip id="seller.documents.redacted-caption" />}
          original={previewOriginal}
          renderUnreadableOptions={(open) => unreadableOptionsFor(previewDoc, open)}
          renderManage={(close) => (
            <SellerManageDocumentDialog roomId={roomId} document={previewDoc} onClose={close} />
          )}
        />
      ) : null}

      {managedDoc ? (
        <SellerManageDocumentDialog
          roomId={roomId}
          document={managedDoc}
          onClose={() => setManageId(null)}
        />
      ) : null}
    </div>
  );
}

/** What the seller sees and may do for one document: its folder, status and
 * basis (which they may correct), a readable copy when we could not read it,
 * where it came from, and its versions. */
function SellerDocumentDetails({
  roomId,
  document: selected,
  onOpenDocument,
  onView,
  onManage,
  basis: setBasis,
  unreadableOptions,
}: {
  roomId: string;
  document: RoomDocument;
  onOpenDocument: (documentId: string) => void;
  onView?: () => void;
  /** Opens "Manage document" (folder and date); absent for a document the AI never files. */
  onManage?: () => void;
  basis: ReturnType<typeof useSetStatementBasis>;
  /** What the seller can do about a file we couldn't read (`UnreadableFileOptions`). */
  unreadableOptions?: ReactNode;
}) {
  return (
    <>
      <VersionSuggestionsPanel
        roomId={roomId}
        documentId={selected.id}
        onOpenDocument={onOpenDocument}
        className="mb-4"
      />
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Document</dt>
          <dd className="flex items-center gap-2 font-medium">
            <span className="min-w-0 break-words">{selected.name}</span>
            {typeof selected.version === 'number' && selected.version > 1 ? (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                v{selected.version}
              </span>
            ) : null}
            {onView ? (
              <Button variant="outline" size="sm" className="ml-auto shrink-0" onClick={onView}>
                View file
              </Button>
            ) : null}
          </dd>
        </div>
        {selected.description ? (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">What this is</dt>
            <dd>{selected.description}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Folder</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 break-words">
              {selected.categoryPath.length > 0
                ? categoryPathLabel(selected.categoryPath)
                : 'Not filed in a folder yet'}
            </span>
            {onManage ? (
              <Button variant="outline" size="sm" className="shrink-0" onClick={onManage}>
                Manage document
              </Button>
            ) : null}
          </dd>
        </div>
        {showsStatementBasis(selected) ? (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Accounting basis</dt>
            <dd>
              <StatementBasisEditor
                key={selected.id}
                value={selected.statementBasis}
                pending={setBasis.isPending}
                error={
                  setBasis.error
                    ? setBasis.error instanceof ApiError
                      ? setBasis.error.message
                      : 'Saving the basis failed.'
                    : null
                }
                onSave={(basis, onSaved) =>
                  setBasis.mutate(
                    { documentId: selected.id, body: { basis } },
                    { onSuccess: onSaved },
                  )
                }
              />
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Status</dt>
          <dd>
            <SellerDocStatus status={selected.status} unreadable={selected.unreadable} />
          </dd>
        </div>
        {selected.unreadable ? (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">We couldn’t read it</dt>
            <dd className="space-y-2">
              <p>{selected.unreadable.message}</p>
              {unreadableOptions}
            </dd>
          </div>
        ) : null}
        {selected.archive ? (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Unpacked</dt>
            <dd>
              {selected.archive.files === 1
                ? '1 file inside was filed on its own.'
                : `${selected.archive.files} files inside were filed on their own.`}
            </dd>
          </div>
        ) : null}
        {selected.fromArchive ? (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Came from</dt>
            <dd className="break-words">{selected.fromArchive.name ?? 'A zip or email'}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Submitted</dt>
          <dd>{formatDateTime(selected.createdAt)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Submitted by</dt>
          <dd>
            <PersonDisplay email={selected.uploadedBy} fallback="—" size="sm" />
          </dd>
        </div>
      </dl>
      {selected.downloadReady ? (
        <div className="mt-4 text-sm">
          <SellerVersionHistory roomId={roomId} documentId={selected.id} />
        </div>
      ) : null}
    </>
  );
}
